import { useId } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { Icon } from "../design";
import useTranslator from "../../hooks/useTranslator";
import "./lockedFeature.css";

// Missing keys are registered by the translator (with this context) and
// translated through the locales, like everywhere else in the project.
const useLockedText = () => {
  const { _t } = useTranslator();
  return {
    short: _t("notEnabledForWorkspace", "Not enabled"),
    long: _t("notEnabledForWorkspaceLong", "Not enabled for this workspace"),
  };
};

// Tooltip for an action that is blocked (a disabled button, or a click that
// does nothing). Call it once per component:
//   const lockedTip = useLockedTooltip();
//   <button {...lockedTip.props(isLocked)}>...</button>
//   {lockedTip.tooltip}    (once, anywhere in the component)
// `props(false)` adds nothing, so it is safe to spread on any element.
export const useLockedTooltip = () => {
  const id = useId();
  const { long } = useLockedText();
  return {
    props: (locked) => (locked ? { "data-tooltip-id": id } : {}),
    tooltip: (
      <ReactTooltip
        id={id}
        place="top"
        render={() => <p>{long}</p>}
        className="react-tooltip-styling"
      />
    ),
  };
};

// Shown instead of a feature the workspace has not switched on.
// - <LockedBadge />: small pill, for the value of a header stat.
// - <LockedIcon />: icon only, for a tag inside a list row.
// - <LockedFeature>: puts a faded, unclickable overlay on a whole card.
export const LockedBadge = () => {
  const { short } = useLockedText();
  return (
    <span className="locked-badge">
      <Icon name="lock" size={14} />
      {short}
    </span>
  );
};

export const LockedIcon = ({ className = "" }) => {
  const { long } = useLockedText();
  return (
    <span className={`locked-icon ${className}`.trim()} title={long}>
      <Icon name="lock" size={16} />
    </span>
  );
};

const LockedFeature = ({ locked, children }) => {
  const { long } = useLockedText();
  if (!locked) return children;
  return (
    <div className="locked-feature">
      <div className="locked-feature__content" aria-hidden="true">
        {children}
      </div>
      <div className="locked-feature__message">
        <Icon name="lock" size={20} />
        <span>{long}</span>
      </div>
    </div>
  );
};

export default LockedFeature;
